import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ChunkLoadRecovery, DecisaoRecuperacao } from './chunk-load-recovery';
import { diagnosticarChunkLoad, observarRestauracaoDeDocumento } from './chunk-load-diagnostics';
import { OrderFlowTraceService } from './order-flow-trace.service';
import { ehRuido } from './error-noise';
import { SentryService } from './sentry/sentry.service';

/** Erro já desembrulhado e normalizado — o que a telemetria e a decisão usam. */
interface ErroNormalizado {
  name: string;
  message: string;
  stackHead: string;
}

/**
 * OrderFlowErrorHandler
 *
 * ErrorHandler global: toda exceção não-capturada do app vira um
 * `ClientAppError` no funil de saúde (health_15m → alerta js_error_spike).
 *
 * Por que existe: um bug em produção aparece como exceção JS MINUTOS antes de
 * aparecer como queda de conversão — é o alerta mais cedo que o monitor tem.
 * O SDK de mercado (Sentry ~25KB, CloudWatch RUM ~40KB) foi rejeitado pelo
 * orçamento de bundle (o app acabou de sair de 628→336KB); isto aqui custa
 * ~2KB sobre o transporte que já existe.
 *
 * Regras de sobrevivência:
 *  - NUNCA lança (um error handler que lança cria loop infinito);
 *  - SEMPRE delega o console.error (o comportamento do handler default do
 *    Angular) — a telemetria é um efeito colateral, não um substituto;
 *  - throttle client-side em DUAS camadas, porque são dois problemas
 *    diferentes: (1) dedupe por ASSINATURA em escala log (1ª, 10ª, 100ª…),
 *    contra o mesmo erro repetindo para sempre; (2) teto de MAX_PER_WINDOW
 *    emissões por janela de WINDOW_MS, contra uma tempestade de assinaturas
 *    DISTINTAS. O `count` viaja no payload, então o volume nunca se perde —
 *    o que se corta é a repetição.
 */
@Injectable()
export class OrderFlowErrorHandler implements ErrorHandler {
  /** Janela do throttle (ms) e teto de emissões por janela. */
  static readonly WINDOW_MS = 60000;
  static readonly MAX_PER_WINDOW = 10;

  private windowStart = 0;
  private emittedInWindow = 0;
  private countsBySig = new Map<string, number>();

  /**
   * Recuperação de ChunkLoadError. Propriedade, e não parâmetro do construtor
   * (nem com valor default): o Angular resolve TODO parâmetro declarado via
   * `design:paramtypes`, e um `ChunkLoadRecovery` ali — mesmo opcional — viraria
   * NullInjectorError, porque não é provider de módulo nenhum. Pública só para
   * o teste substituir; ninguém mais deve escrever nela.
   */
  recovery = new ChunkLoadRecovery();

  /**
   * Injector em vez do serviço direto: o ErrorHandler é instanciado ANTES de
   * todo o resto do app (é o primeiro provider que o Angular resolve), e uma
   * dependência direta forçaria a criação precoce de toda a cadeia
   * (RestaurantService etc.) — além de transformar um erro de DI do serviço de
   * telemetria num crash do handler de erros. Lazy até o primeiro erro.
   */
  constructor(private injector: Injector) {
    // O `pageshow` que interessa é o da VOLTA do bfcache, e quem registra
    // depois já o perdeu — por isso aqui, no provider que o Angular resolve
    // primeiro, ainda na carga original. Custa um ouvinte e nunca lança; o
    // retorno é ignorado de propósito (só o spec precisa dele, como controle
    // positivo de que o registro aconteceu de verdade).
    observarRestauracaoDeDocumento();
  }

  handleError(error: any): void {
    let decisao: DecisaoRecuperacao = '';
    try {
      const info = OrderFlowErrorHandler.normalizar(error);
      // Decidir ANTES de reportar: o veredito vai no payload, senão o evento
      // que explica a recuperação morre junto com a página no reload.
      // Em try próprio: recuperação quebrada não pode calar a telemetria — ela
      // é a razão de o handler existir, a recuperação é o extra.
      try {
        decisao = this.recovery.decidir(info.name, info.message);
      } catch (e) {
        decisao = '';
      }
      this.report(error, info, decisao);
    } catch (e) {
      // Telemetria de erro que erra morre aqui — nunca vira o segundo erro.
    }
    // Comportamento do ErrorHandler default do Angular: o erro continua
    // visível no console (e nos logs do Cypress/CI) exatamente como antes.
    // eslint-disable-next-line no-console
    console.error(error);

    // Reload por ÚLTIMO, e só depois do console/telemetria: o `sendBeacon` já
    // saiu (o browser o entrega mesmo com a página sendo descarregada) e quem
    // depura ainda vê o erro original.
    try {
      if (decisao === 'reload') this.recovery.recarregar();
    } catch (e) {
      // Recuperação que falha deixa a tela como está hoje — nunca vira erro novo.
    }
  }

  /**
   * Emite nesta ocorrência da assinatura?
   *
   * Escala logarítmica: 1ª, 10ª, 100ª, 1000ª… A 1ª prova que o erro existe; as
   * seguintes medem a escalada (loop) sem inundar. O `count` viaja no payload,
   * então o volume real nunca se perde — o que se corta é a repetição.
   *
   * O primeiro desenho (teto por minuto, com o mapa de assinaturas limpo a cada
   * janela) reemitia um erro recorrente para sempre: 26 mil eventos de UMA
   * assinatura em um dia, 17% de todo o tráfego, sendo um `Timeout` do SDK do
   * OneSignal que acontece no boot de quase todo device. Contar não exige
   * reenviar.
   */
  private static deveEmitir(count: number): boolean {
    if (count < 10) return count === 1;
    if (count < 100) return count === 10;
    if (count < 1000) return count === 100;
    return count % 1000 === 0;
  }

  /**
   * É ruído conhecido — controle de fluxo do próprio app ou terceiro — e NÃO
   * um erro de aplicação?
   *
   * A lista mudou de casa: mora em `error-noise.ts` desde que o Sentry passou a
   * ser um segundo destino do mesmo erro. Os dois filtram pelo MESMO conjunto
   * (aqui, e via `ignoreErrors` do SDK); duplicar a lista faria um painel
   * afogar enquanto o outro fica limpo, e ela custou um dia de produção para
   * ser levantada — dois itens dela eram ~15% de todo o tráfego de telemetria.
   * O método fica como fachada estática porque é assim que o resto da classe
   * (e o spec) já o chamava.
   */
  private static ehRuido(message: string, stackHead: string): boolean {
    return ehRuido(message, stackHead);
  }

  /**
   * Tira o embrulho "Uncaught (in promise): ..." do Angular, que guarda o erro
   * de verdade em `rejection`.
   *
   * Extraído de `normalizar` porque agora tem um segundo consumidor: o Sentry
   * precisa do OBJETO original (com stack) para desminificar e para agrupar —
   * mandar o embrulho colapsaria todas as rejeições numa issue só.
   */
  static desembrulhar(error: any): any {
    return (error && error.rejection) ? error.rejection : error;
  }

  /**
   * Desembrulha e normaliza o erro — desembrulhar é o que mantém a assinatura
   * (e, portanto, o dedupe) estável.
   */
  private static normalizar(error: any): ErroNormalizado {
    const err = OrderFlowErrorHandler.desembrulhar(error);
    return {
      name: String((err && err.name) || 'Error').slice(0, 60),
      message: String((err && err.message) || err || '').slice(0, 300),
      stackHead: String((err && err.stack) || '')
        .split('\n').slice(0, 3).join(' | ').slice(0, 180),
    };
  }

  private report(original: any, info: ErroNormalizado, decisao: DecisaoRecuperacao): void {
    const now = Date.now();
    if (now - this.windowStart > OrderFlowErrorHandler.WINDOW_MS) {
      this.windowStart = now;
      this.emittedInWindow = 0;
      // countsBySig NÃO é limpo: a contagem por assinatura vale pela vida da
      // página. Limpá-la aqui era o que ressuscitava o mesmo erro a cada minuto.
    }

    const { name, message, stackHead } = info;

    // Ruído conhecido (controle de fluxo / terceiro): não vira telemetria.
    // Fica ANTES do dedupe para nem consumir contador/janela.
    if (OrderFlowErrorHandler.ehRuido(message, stackHead)) return;

    const sig = `${name}|${message.slice(0, 120)}|${stackHead.slice(0, 60)}`;
    const count = (this.countsBySig.get(sig) || 0) + 1;
    this.countsBySig.set(sig, count);

    if (!OrderFlowErrorHandler.deveEmitir(count)) return;
    // Teto por minuto continua como segunda barreira — protege do caso oposto
    // (muitas assinaturas DISTINTAS de uma vez, onde o dedupe não ajuda).
    if (this.emittedInWindow >= OrderFlowErrorHandler.MAX_PER_WINDOW) return;
    this.emittedInWindow++;

    let route = '';
    try {
      route = (window.location.pathname + window.location.hash).slice(0, 80);
    } catch (e) { /* route fica vazia */ }

    const out: any = {
      name,
      message,
      message_hash: message,   // shape hash8 na allowlist → vira fnv1a de 8 hex
      stack_head: stackHead,
      route,
      count,
    };
    // Só nos ChunkLoadError o campo existe: é o que separa "recarregou e o
    // cliente seguiu" de "bateu no freio e ficou na tela quebrada". Ausente no
    // resto para não inflar o payload de todo erro.
    if (decisao) out.recovery = decisao;

    // Diagnóstico da falha de chunk. Condicionado ao erro SER de chunk, e não a
    // `if (decisao)`: quando o recuperador lança, o catch lá em cima zera o
    // veredito — e aí perderíamos o diagnóstico exatamente na ocorrência mais
    // suspeita. Medir por que o chunk falhou não pode depender de o recuperador
    // estar saudável; são responsabilidades separadas.
    //
    // Vai o erro DESEMBRULHADO: `type` e `request` moram no erro real, o
    // embrulho `Uncaught (in promise)` do Angular não os carrega.
    if (ChunkLoadRecovery.ehChunkLoadError(name, message)) {
      Object.assign(out, diagnosticarChunkLoad(OrderFlowErrorHandler.desembrulhar(original)));
    }

    // Lazy: só resolve o serviço quando há um erro de verdade a reportar.
    const trace = this.injector.get(OrderFlowTraceService);
    trace.emitHealth('ClientAppError', out, 'OrderFlowErrorHandler.handleError');

    // Segundo destino: o Sentry. NO MESMO PONTO do funil, de propósito — o
    // throttle acima (dedupe log-scale + teto por janela) foi calibrado em
    // produção contra um erro que gerou 26 mil eventos num dia, e o Sentry
    // cobra por evento. Reusar o mesmo gate é o que impede o painel novo de
    // repetir o problema que o painel antigo já resolveu.
    //
    // O que vai daqui é o erro ORIGINAL (não o normalizado): o Sentry precisa do
    // objeto com stack para desminificar pelo sourcemap — é justamente o que ele
    // acrescenta ao `stack_head` truncado que o funil grava.
    this.reportarSentry(original, out);
  }

  /**
   * Envia ao Sentry sem nunca deixar a falha vazar. Em try próprio e com o
   * serviço resolvido de forma opcional porque o funil de saúde não pode
   * depender do Sentry: se o serviço não estiver disponível (teste, injector
   * enxuto, DSN vazio), o `emitHealth` acima já aconteceu e é ele que alimenta
   * o alerta do Discord.
   */
  private reportarSentry(original: any, out: any): void {
    try {
      const sentry: SentryService | null = this.injector.get(SentryService, null);
      if (!sentry) return;
      sentry.capture(OrderFlowErrorHandler.desembrulhar(original), {
        count: out.count,
        route: out.route,
        recovery: out.recovery,
      });
    } catch (e) {
      // Sentry indisponível não pode virar erro novo dentro do error handler.
    }
  }
}
